/**
 * Zuno Studio API — RTK Query Endpoints
 */
import { baseApi } from './baseApi';

const zunoStudioApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    // ── Agent CRUD ──
    listAgents: builder.query({
      query: (params) => ({ url: '/studio', params }),
      providesTags: ['ZunoAgent'],
    }),
    getAgent: builder.query({
      query: (agentId) => `/studio/${agentId}`,
      providesTags: (_r, _e, id) => [{ type: 'ZunoAgent', id }],
    }),
    createAgent: builder.mutation({
      query: (body) => ({ url: '/studio', method: 'POST', body }),
      invalidatesTags: ['ZunoAgent'],
    }),
    updateAgent: builder.mutation({
      query: ({ agentId, ...body }) => ({ url: `/studio/${agentId}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { agentId }) => ['ZunoAgent', { type: 'ZunoAgent', id: agentId }],
    }),
    deleteAgent: builder.mutation({
      query: (agentId) => ({ url: `/studio/${agentId}`, method: 'DELETE' }),
      invalidatesTags: ['ZunoAgent'],
    }),
    duplicateAgent: builder.mutation({
      query: (agentId) => ({ url: `/studio/${agentId}/duplicate`, method: 'POST' }),
      invalidatesTags: ['ZunoAgent'],
    }),
    publishAgent: builder.mutation({
      query: (agentId) => ({ url: `/studio/${agentId}/publish`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => ['ZunoAgent', { type: 'ZunoAgent', id }],
    }),
    unpublishAgent: builder.mutation({
      query: (agentId) => ({ url: `/studio/${agentId}/unpublish`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => ['ZunoAgent', { type: 'ZunoAgent', id }],
    }),
    // Fix #1: Avatar upload
    uploadAgentAvatar: builder.mutation({
      query: ({ agentId, formData }) => ({ url: `/studio/${agentId}/avatar`, method: 'POST', body: formData, formData: true }),
      invalidatesTags: (_r, _e, { agentId }) => [{ type: 'ZunoAgent', id: agentId }],
    }),
    // Fix #2: Favorites
    toggleFavoriteAgent: builder.mutation({
      query: (agentId) => ({ url: `/studio/${agentId}/favorite`, method: 'POST' }),
      invalidatesTags: ['ZunoAgent'],
    }),

    // ── Knowledge ──
    listKnowledge: builder.query({
      query: (agentId) => `/studio/${agentId}/knowledge`,
      providesTags: (_r, _e, id) => [{ type: 'AgentKnowledge', id }],
    }),
    getKnowledgeSummary: builder.query({
      query: (agentId) => `/studio/${agentId}/knowledge/summary`,
      providesTags: (_r, _e, id) => [{ type: 'AgentKnowledge', id }],
    }),
    addTextKnowledge: builder.mutation({
      query: ({ agentId, ...body }) => ({ url: `/studio/${agentId}/knowledge`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { agentId }) => [{ type: 'AgentKnowledge', id: agentId }],
    }),
    uploadFileKnowledge: builder.mutation({
      query: ({ agentId, formData }) => ({
        url: `/studio/${agentId}/knowledge/upload`,
        method: 'POST',
        body: formData,
        formData: true,
      }),
      invalidatesTags: (_r, _e, { agentId }) => [{ type: 'AgentKnowledge', id: agentId }],
    }),
    takeDataSnapshot: builder.mutation({
      query: ({ agentId, module }) => ({ url: `/studio/${agentId}/knowledge/snapshot`, method: 'POST', body: { module } }),
      invalidatesTags: (_r, _e, { agentId }) => [{ type: 'AgentKnowledge', id: agentId }],
    }),
    updateKnowledge: builder.mutation({
      query: ({ agentId, knowledgeId, ...body }) => ({ url: `/studio/${agentId}/knowledge/${knowledgeId}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { agentId }) => [{ type: 'AgentKnowledge', id: agentId }],
    }),
    deleteKnowledge: builder.mutation({
      query: ({ agentId, knowledgeId }) => ({ url: `/studio/${agentId}/knowledge/${knowledgeId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { agentId }) => [{ type: 'AgentKnowledge', id: agentId }],
    }),
    // Fix #10: File re-upload
    reuploadFile: builder.mutation({
      query: ({ agentId, knowledgeId, formData }) => ({ url: `/studio/${agentId}/knowledge/${knowledgeId}/reupload`, method: 'PUT', body: formData, formData: true }),
      invalidatesTags: (_r, _e, { agentId }) => [{ type: 'AgentKnowledge', id: agentId }],
    }),
    reorderKnowledge: builder.mutation({
      query: ({ agentId, orderedIds }) => ({ url: `/studio/${agentId}/knowledge/reorder`, method: 'PUT', body: { orderedIds } }),
      invalidatesTags: (_r, _e, { agentId }) => [{ type: 'AgentKnowledge', id: agentId }],
    }),

    // ── Templates ──
    listAgentTemplates: builder.query({
      query: () => '/studio/templates',
      providesTags: ['AgentTemplate'],
    }),
    getAgentTemplate: builder.query({
      query: (templateId) => `/studio/templates/${templateId}`,
      providesTags: ['AgentTemplate'],
    }),
    createAgentFromTemplate: builder.mutation({
      query: (templateId) => ({ url: `/studio/templates/${templateId}/use`, method: 'POST' }),
      invalidatesTags: ['ZunoAgent'],
    }),

    // ── Chat (Phase 2) ──
    sendAgentMessage: builder.mutation({
      query: ({ agentId, message, conversationId }) => ({
        url: `/studio/${agentId}/chat`, method: 'POST',
        body: { message, conversationId },
      }),
      invalidatesTags: (_r, _e, { agentId }) => [{ type: 'AgentConversation', id: agentId }],
    }),
    listAgentConversations: builder.query({
      query: ({ agentId, ...params }) => ({ url: `/studio/${agentId}/conversations`, params }),
      providesTags: (_r, _e, { agentId }) => [{ type: 'AgentConversation', id: agentId }],
    }),
    getAgentConversation: builder.query({
      query: ({ agentId, convId }) => `/studio/${agentId}/conversations/${convId}`,
      providesTags: (_r, _e, { convId }) => [{ type: 'AgentConversation', id: convId }],
    }),
    archiveConversation: builder.mutation({
      query: ({ agentId, convId }) => ({ url: `/studio/${agentId}/conversations/${convId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { agentId }) => [{ type: 'AgentConversation', id: agentId }],
    }),
    submitConversationFeedback: builder.mutation({
      query: ({ agentId, convId, satisfaction }) => ({
        url: `/studio/${agentId}/conversations/${convId}/feedback`, method: 'POST',
        body: { satisfaction },
      }),
      invalidatesTags: (_r, _e, { convId }) => [{ type: 'AgentConversation', id: convId }],
    }),

    // ── Actions (Phase 2) ──
    listPendingActions: builder.query({
      query: (params) => ({ url: '/studio/actions/pending', params }),
      providesTags: ['AgentAction'],
    }),
    listActionHistory: builder.query({
      query: (params) => ({ url: '/studio/actions/history', params }),
      providesTags: ['AgentAction'],
    }),
    approveStudioAction: builder.mutation({
      query: (actionId) => ({ url: `/studio/actions/${actionId}/approve`, method: 'POST' }),
      invalidatesTags: ['AgentAction'],
    }),
    rejectAction: builder.mutation({
      query: ({ actionId, reason }) => ({ url: `/studio/actions/${actionId}/reject`, method: 'POST', body: { reason } }),
      invalidatesTags: ['AgentAction'],
    }),

    // ── F2 · preview at the decision point ──
    // keepUnusedDataFor: 0 — a preview is only valid at the moment it is
    // fetched. Serving a cached one to the next reviewer would show them a
    // world that may have changed, and they would trust it.
    previewStudioAction: builder.query({
      query: (actionId) => ({ url: `/studio/actions/${actionId}/preview` }),
      transformResponse: (r) => r?.data ?? r,
      keepUnusedDataFor: 0,
    }),

    // ── C1 · registry demand report ──
    registryDemand: builder.query({
      query: (params) => ({ url: '/studio/registry/demand', params }),
      transformResponse: (r) => r?.data ?? r,
    }),

    // ── F4 · evaluation harness ──
    listEvalSuites: builder.query({
      query: (params) => ({ url: '/studio/evals', params }),
      transformResponse: (r) => r?.data ?? r,
      providesTags: ['EvalSuite'],
    }),
    createEvalSuite: builder.mutation({
      query: (body) => ({ url: '/studio/evals', method: 'POST', body }),
      transformResponse: (r) => r?.data ?? r,
      invalidatesTags: ['EvalSuite'],
    }),
    updateEvalSuite: builder.mutation({
      query: ({ suiteId, ...body }) => ({ url: `/studio/evals/${suiteId}`, method: 'PATCH', body }),
      transformResponse: (r) => r?.data ?? r,
      invalidatesTags: ['EvalSuite'],
    }),
    deleteEvalSuite: builder.mutation({
      query: (suiteId) => ({ url: `/studio/evals/${suiteId}`, method: 'DELETE' }),
      transformResponse: (r) => r?.data ?? r,
      invalidatesTags: ['EvalSuite'],
    }),
    runEvalSuite: builder.mutation({
      query: (suiteId) => ({ url: `/studio/evals/${suiteId}/run`, method: 'POST' }),
      transformResponse: (r) => r?.data ?? r,
      invalidatesTags: ['EvalSuite', 'EvalRun'],
    }),
    listEvalRuns: builder.query({
      query: (params) => ({ url: '/studio/evals/runs', params }),
      transformResponse: (r) => r?.data ?? r,
      providesTags: ['EvalRun'],
    }),
    getEvalRun: builder.query({
      query: (runId) => ({ url: `/studio/evals/runs/${runId}` }),
      transformResponse: (r) => r?.data ?? r,
    }),

    // ── F5 · multi-agent orchestration ──
    planOrchestration: builder.mutation({
      query: (request) => ({ url: '/studio/orchestrate/plan', method: 'POST', body: { request } }),
      transformResponse: (r) => r?.data ?? r,
    }),
    orchestrate: builder.mutation({
      query: (request) => ({ url: '/studio/orchestrate', method: 'POST', body: { request } }),
      transformResponse: (r) => r?.data ?? r,
    }),

    // ── F3 · action ledger and undo ──
    actionLedger: builder.query({
      query: (params) => ({ url: '/studio/actions/ledger', params }),
      transformResponse: (r) => r?.data ?? r,
      providesTags: ['AgentAction'],
    }),
    undoStudioAction: builder.mutation({
      query: (actionId) => ({ url: `/studio/actions/${actionId}/undo`, method: 'POST' }),
      transformResponse: (r) => r?.data ?? r,
      invalidatesTags: ['AgentAction'],
    }),

    // ── Playground (Phase 3) ──
    playgroundChat: builder.mutation({
      query: ({ agentId, message }) => ({
        url: `/studio/${agentId}/playground`, method: 'POST', body: { message },
      }),
    }),

    // ── Analytics (Phase 3) ──
    getStudioOverview: builder.query({
      query: (params) => ({ url: '/studio/analytics/overview', params }),
      providesTags: ['StudioAnalytics'],
    }),
    getAgentAnalytics: builder.query({
      query: ({ agentId, ...params }) => ({ url: `/studio/analytics/agents/${agentId}`, params }),
      providesTags: ['StudioAnalytics'],
    }),
    getUsageTimeSeries: builder.query({
      query: (params) => ({ url: '/studio/analytics/usage', params }),
      providesTags: ['StudioAnalytics'],
    }),
    getTopQueries: builder.query({
      query: (params) => ({ url: '/studio/analytics/top-queries', params }),
      providesTags: ['StudioAnalytics'],
    }),
    // Fix #5: Failed interactions
    getFailedInteractions: builder.query({
      query: (params) => ({ url: '/studio/analytics/failed', params }),
      providesTags: ['StudioAnalytics'],
    }),

    // ── Custom Template (Phase 3) ──
    createCustomTemplate: builder.mutation({
      query: (agentId) => ({ url: '/studio/templates/custom', method: 'POST', body: { agentId } }),
      invalidatesTags: ['AgentTemplate', 'ZunoAgent'],
    }),

    // ── Phase 4 — Guardrail Logs ──
    listGuardrailLogs: builder.query({
      query: (params) => ({ url: '/studio/guardrails/logs', params }),
      providesTags: ['GuardrailLog'],
    }),
    getGuardrailStats: builder.query({
      query: (params) => ({ url: '/studio/guardrails/stats', params }),
      providesTags: ['GuardrailLog'],
    }),

    // ── Phase 4 — Agent Versions ──
    getAgentVersions: builder.query({
      query: (agentId) => `/studio/${agentId}/versions`,
      providesTags: (_r, _e, id) => [{ type: 'AgentVersion', id }],
    }),
    saveAgentVersion: builder.mutation({
      query: ({ agentId, changeNote }) => ({ url: `/studio/${agentId}/versions`, method: 'POST', body: { changeNote } }),
      invalidatesTags: (_r, _e, { agentId }) => [{ type: 'AgentVersion', id: agentId }],
    }),
    revertAgentVersion: builder.mutation({
      query: ({ agentId, versionIndex }) => ({ url: `/studio/${agentId}/versions/${versionIndex}/revert`, method: 'POST' }),
      invalidatesTags: (_r, _e, { agentId }) => [{ type: 'AgentVersion', id: agentId }, { type: 'ZunoAgent', id: agentId }],
    }),

    // ── Phase 4 — Schedules ──
    listSchedules: builder.query({
      query: (agentId) => `/studio/${agentId}/schedules`,
      providesTags: ['AgentSchedule'],
    }),
    createSchedule: builder.mutation({
      query: ({ agentId, ...body }) => ({ url: `/studio/${agentId}/schedules`, method: 'POST', body }),
      invalidatesTags: ['AgentSchedule'],
    }),
    updateSchedule: builder.mutation({
      query: ({ agentId, scheduleId, ...body }) => ({ url: `/studio/${agentId}/schedules/${scheduleId}`, method: 'PUT', body }),
      invalidatesTags: ['AgentSchedule'],
    }),
    deleteSchedule: builder.mutation({
      query: ({ agentId, scheduleId }) => ({ url: `/studio/${agentId}/schedules/${scheduleId}`, method: 'DELETE' }),
      invalidatesTags: ['AgentSchedule'],
    }),

    // ── Phase 4 — Export / Import / Benchmarks ──
    exportAgent: builder.query({
      query: (agentId) => `/studio/${agentId}/export`,
    }),
    importAgent: builder.mutation({
      query: (config) => ({ url: '/studio/import', method: 'POST', body: config }),
      invalidatesTags: ['ZunoAgent'],
    }),
    getAgentBenchmarks: builder.query({
      query: (agentId) => `/studio/${agentId}/benchmarks`,
      providesTags: ['StudioAnalytics'],
    }),

    // ── Intelligence AI Helpers ──
    generatePrompt: builder.mutation({
      query: (body) => ({ url: '/studio/ai/generate-prompt', method: 'POST', body }),
    }),
    recommendModules: builder.mutation({
      query: (body) => ({ url: '/studio/ai/recommend-modules', method: 'POST', body }),
    }),
    suggestGuardrails: builder.mutation({
      query: (body) => ({ url: '/studio/ai/suggest-guardrails', method: 'POST', body }),
    }),
    getQualityScore: builder.query({
      query: (agentId) => `/studio/${agentId}/quality-score`,
      providesTags: (_r, _e, id) => [{ type: 'ZunoAgent', id }],
    }),
    detectKnowledgeGaps: builder.query({
      query: (agentId) => `/studio/${agentId}/knowledge-gaps`,
    }),
    generateInsights: builder.query({
      query: () => '/studio/ai/insights',
      providesTags: ['StudioAnalytics'],
    }),

    // ── Flow Builder ──
    runFlowPreview: builder.mutation({
      query: (agentId) => ({ url: `/studio/${agentId}/flow/run`, method: 'POST' }),
    }),
    listFlowExecutions: builder.query({
      query: (agentId) => `/studio/${agentId}/flow/executions`,
    }),
    listNodeTemplates: builder.query({
      query: () => '/studio/flow/node-templates',
    }),
    saveNodeTemplate: builder.mutation({
      query: (body) => ({ url: '/studio/flow/node-templates', method: 'POST', body }),
    }),
    listMarketplaceFlows: builder.query({
      query: () => '/studio/flow/marketplace',
    }),
    importMarketplaceFlow: builder.mutation({
      query: (flowId) => ({ url: `/studio/flow/marketplace/${flowId}/import`, method: 'POST' }),
      invalidatesTags: ['ZunoAgent'],
    }),
    aiConfigureNode: builder.mutation({
      query: (body) => ({ url: '/studio/flow/ai-configure-node', method: 'POST', body }),
    }),
    copilotAssist: builder.mutation({
      query: (body) => ({ url: '/studio/ai/copilot', method: 'POST', body }),
    }),

    // ── Workflow graph validation and resumable runs (2.29 / 2.32) ──
    validateFlow: builder.mutation({
      query: ({ agentId, nodes, edges }) => ({
        url: `/studio/${agentId}/flow/validate`, method: 'POST', body: { nodes, edges },
      }),
    }),
    resumeFlowRun: builder.mutation({
      query: (executionId) => ({ url: `/studio/flow/executions/${executionId}/resume`, method: 'POST' }),
    }),

    // ── Autonomous kill switch and shadow run (2.24 / 2.25) ──
    setKillSwitch: builder.mutation({
      query: ({ agentId, ...body }) => ({ url: `/studio/${agentId}/kill-switch`, method: 'PUT', body }),
      invalidatesTags: ['Agent'],
    }),
    runShadowCycle: builder.mutation({
      query: (agentId) => ({ url: `/studio/${agentId}/shadow-run`, method: 'POST' }),
      invalidatesTags: ['Agent'],
    }),
    reviewShadowRun: builder.mutation({
      query: (agentId) => ({ url: `/studio/${agentId}/shadow-run/review`, method: 'POST' }),
      invalidatesTags: ['Agent'],
    }),

    // ── Co-Pilot resolution, action stream and privacy (2.11 / 2.16 / 2.17) ──
    // The server resolves WHICH copilot is bound to the current route and which
    // surfaces it may use, so the UI never has to guess or hardcode a mapping.
    resolveCopilot: builder.query({
      query: (route) => ({ url: '/studio/copilot/resolve', params: { route } }),
      // Which copilot is bound to a route changes only when an agent is edited,
      // so this does not need re-fetching on every navigation. Cached for five
      // minutes per route; the tag invalidates it when an agent is saved.
      keepUnusedDataFor: 300,
      providesTags: ['Agent'],
    }),
    recordCopilotAction: builder.mutation({
      query: (body) => ({ url: '/studio/copilot/stream', method: 'POST', body }),
    }),
    setCopilotStreamPreference: builder.mutation({
      query: (body) => ({ url: '/studio/copilot/stream/preference', method: 'PUT', body }),
    }),
    clearCopilotStream: builder.mutation({
      query: () => ({ url: '/studio/copilot/stream', method: 'DELETE' }),
    }),
    // Autonomous agent
    parseNLCondition: builder.mutation({
      query: (body) => ({ url: '/studio/ai/parse-condition', method: 'POST', body }),
    }),
    getAutonomousDashboard: builder.query({
      query: () => '/studio/autonomous/dashboard',
      providesTags: ['StudioAnalytics'],
    }),
    listEventLogs: builder.query({
      query: (params) => ({ url: '/studio/autonomous/event-log', params }),
      providesTags: ['StudioAnalytics'],
    }),
    dryRunAutonomous: builder.mutation({
      query: (agentId) => ({ url: `/studio/${agentId}/autonomous/dry-run`, method: 'POST' }),
    }),
    runWatchdog: builder.query({
      query: () => '/studio/autonomous/watchdog',
    }),
  }),
});

export const {
  useValidateFlowMutation,
  useResumeFlowRunMutation,
  useSetKillSwitchMutation,
  useRunShadowCycleMutation,
  useReviewShadowRunMutation,
  useResolveCopilotQuery,
  useRecordCopilotActionMutation,
  useSetCopilotStreamPreferenceMutation,
  useClearCopilotStreamMutation,
  useListAgentsQuery,
  useGetAgentQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  useDeleteAgentMutation,
  useDuplicateAgentMutation,
  usePublishAgentMutation,
  useUnpublishAgentMutation,
  useUploadAgentAvatarMutation,
  useToggleFavoriteAgentMutation,
  useListKnowledgeQuery,
  useGetKnowledgeSummaryQuery,
  useAddTextKnowledgeMutation,
  useUploadFileKnowledgeMutation,
  useTakeDataSnapshotMutation,
  useUpdateKnowledgeMutation,
  useDeleteKnowledgeMutation,
  useReuploadFileMutation,
  useReorderKnowledgeMutation,
  useListAgentTemplatesQuery,
  useGetAgentTemplateQuery,
  useCreateAgentFromTemplateMutation,
  // Phase 2 — Chat
  useSendAgentMessageMutation,
  useListAgentConversationsQuery,
  useGetAgentConversationQuery,
  useArchiveConversationMutation,
  useSubmitConversationFeedbackMutation,
  // Phase 2 — Actions
  useListPendingActionsQuery,
  useListActionHistoryQuery,
  useApproveStudioActionMutation,
  useRejectActionMutation,
  // Phase 3 — Playground
  usePlaygroundChatMutation,
  // Phase 3 — Analytics
  useGetStudioOverviewQuery,
  useGetAgentAnalyticsQuery,
  useGetUsageTimeSeriesQuery,
  useGetTopQueriesQuery,
  useGetFailedInteractionsQuery,
  // Phase 3 — Custom Template
  useCreateCustomTemplateMutation,
  // Phase 4 — Guardrail Logs
  useListGuardrailLogsQuery,
  useGetGuardrailStatsQuery,
  // Phase 4 — Versions
  useGetAgentVersionsQuery,
  useSaveAgentVersionMutation,
  useRevertAgentVersionMutation,
  // Phase 4 — Schedules
  useListSchedulesQuery,
  useCreateScheduleMutation,
  useUpdateScheduleMutation,
  useDeleteScheduleMutation,
  // Phase 4 — Export/Import/Benchmarks
  useExportAgentQuery,
  useImportAgentMutation,
  useGetAgentBenchmarksQuery,
  // Intelligence AI Helpers
  useGeneratePromptMutation,
  useRecommendModulesMutation,
  useSuggestGuardrailsMutation,
  useGetQualityScoreQuery,
  useDetectKnowledgeGapsQuery,
  useGenerateInsightsQuery,
  // Flow Builder
  useRunFlowPreviewMutation,
  useListFlowExecutionsQuery,
  useListNodeTemplatesQuery,
  useSaveNodeTemplateMutation,
  useListMarketplaceFlowsQuery,
  useImportMarketplaceFlowMutation,
  useAiConfigureNodeMutation,
  useCopilotAssistMutation,
  useParseNLConditionMutation,
  useGetAutonomousDashboardQuery,
  useListEventLogsQuery,
  useDryRunAutonomousMutation,
  useRunWatchdogQuery,
  usePreviewStudioActionQuery, useLazyPreviewStudioActionQuery,
  useActionLedgerQuery, useUndoStudioActionMutation,
  useRegistryDemandQuery,
  useListEvalSuitesQuery, useCreateEvalSuiteMutation, useUpdateEvalSuiteMutation,
  useDeleteEvalSuiteMutation, useRunEvalSuiteMutation,
  useListEvalRunsQuery, useGetEvalRunQuery,
  usePlanOrchestrationMutation, useOrchestrateMutation,
} = zunoStudioApi;

export default zunoStudioApi;